import React, { useEffect, useState } from "react";
import { Button, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LaunchIcon from "@material-ui/icons/Launch";
import config from "../../config/config";
import { buildReviewUrl } from "../../modules/playableReview";
import { postJson } from "../../modules/jobsApi";
import { apiURI } from "../../modules/apiURI";

const useStyles = makeStyles({
  button: {
    minWidth: 0,
    padding: "3px 10px",
    fontSize: "11px",
    fontWeight: 400,
    lineHeight: 1.5,
    textTransform: "none",
    whiteSpace: "nowrap"
  }
});

function reviewFailure(response) {
  if (response.status === 404) return "Review service is not available on this server yet. The backend needs to be updated or restarted.";
  if (response.status === 401 || response.status === 403) return "Review access was denied. Check your session and export permission.";
  if (response.status === 503) return "Could not prepare the review link. Review storage is unavailable; please try again.";
  if (response.error?.error === "INVALID_REVIEW_LINKS") return "Could not prepare the review link. This build list was rejected by the server.";
  return "Could not prepare the review link. Please try again.";
}

/** A prepared anchor keeps opening/copying the review link a normal browser action. */
export default function TaskReviewButton({ baseID, title, entries, disabled }) {
  const classes = useStyles();
  const payload = JSON.stringify({
    baseID: String(baseID),
    title,
    links: entries.map((entry) => entry.url),
    names: entries.map((entry) => entry.label || "")
  });
  const [result, setResult] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const baseUrl = config.playableReviewUrl;

  useEffect(() => {
    let active = true;
    if (JSON.parse(payload).links.length) {
      const input = JSON.parse(payload);
      postJson("playableReview/create", { baseID: input.baseID, links: input.links }, { timeoutMs: 15000 })
        .then((response) => {
          if (!response.ok) throw new Error(reviewFailure(response));
          if (!response.data?.review) throw new Error("Could not prepare the review link. The server returned an incomplete response.");
          return buildReviewUrl(baseUrl, {
            ...input,
            review: { token: response.data.review, api: apiURI }
          });
        })
        .then(
          (url) => {
            if (active) setResult({ payload, url });
          },
          (error) => {
            if (active) setResult({ payload, error: error.name === "AbortError"
              ? "The review request timed out. Please try again."
              : error.message || "Could not prepare the review link. Check your connection and try again." });
          }
        );
    }
    return () => {
      active = false;
    };
  }, [baseUrl, payload, attempt]);

  const current = result?.payload === payload ? result : null;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end" }}>
      <Button
        className={classes.button}
        component="a"
        size="small"
        color="primary"
        variant="contained"
        href={current?.url}
        target="_blank"
        rel="noopener noreferrer"
        disabled={disabled || !entries.length || !current?.url}
        endIcon={<LaunchIcon style={{ fontSize: "14px" }} />}
        title="View all available builds on one scrollable page, in list order"
      >
        Preview in one page
      </Button>
      {current?.error && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", maxWidth: "400px" }}>
          <Typography component="span" style={{ fontSize: "12px", color: "#ef9a9a" }}>{current.error}</Typography>
          <Button size="small" color="primary" disabled={disabled} onClick={() => {
            setResult(null);
            setAttempt((value) => value + 1);
          }}>Retry</Button>
        </span>
      )}
    </span>
  );
}
